/**
 * Accounting periods — one calendar month per organisation.
 *
 * PURE. Nothing here reads a clock or a database; the caller supplies both the date and the
 * periods that exist. See docs/TRUST_ACCOUNTING.md §7 and CLAUDE.md invariant 6.
 *
 * A period is the unit a trust account is signed off in. Once a month is reconciled and a
 * someone permitted to close months has locked it, the figures in it are the figures that were reported — so
 * nothing may change them, and that includes something dated into an earlier month that
 * nobody has locked yet, because an earlier entry moves the balance the locked month opened
 * with.
 */

import { canClosePeriods } from './access.js';
import { formatNzDate, isCalendarDate, previousDay } from './dates.js';
import { abs, formatAmount, type Money } from './money.js';
import { describeBreak } from './reconciliation/breaks.js';
import type { BreakCode } from './reconciliation/threeWay.js';

/**
 * `open` or `locked` — and the screen says "Open" and "Closed".
 *
 * There used to be a third, `reconciling`, that every screen had a label and a colour for and
 * nothing ever wrote. A month being worked towards its close is simply an open month whose last
 * day has passed; that is a fact about the calendar, not a state somebody sets, so 0124 removed it.
 */
export const PERIOD_STATUSES = ['open', 'locked'] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];

/**
 * The first day of a period's month, `YYYY-MM-01`.
 *
 * A period is identified by the month it covers, and a date is the least surprising way to
 * say that: it sorts, it compares, and it subtracts. Year and month as separate integers make
 * "the month after December" a special case in every query that touches it.
 */
export type PeriodStart = string;

const MONTH_START = /^\d{4}-\d{2}-01$/;

export function isPeriodStart(value: string): value is PeriodStart {
  return MONTH_START.test(value) && isCalendarDate(value);
}

/** The period a date falls in. `2026-08-14` → `2026-08-01`. */
export function periodStartFor(date: string): PeriodStart {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(date);
  if (!match) throw new RangeError(`Not a date: ${JSON.stringify(date)}`);
  if (!isCalendarDate(date)) throw new RangeError(`Not a real date: ${JSON.stringify(date)}`);
  return `${match[1]}-${match[2]}-01`;
}

/** The month after this one. Rolls the year over; no special case at December. */
export function nextPeriodStart(start: PeriodStart): PeriodStart {
  const [yearText, monthText] = start.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  return month === 12
    ? `${String(year + 1).padStart(4, '0')}-01-01`
    : `${yearText}-${String(month + 1).padStart(2, '0')}-01`;
}

/**
 * The month before this one. Rolls the year back; no special case at January.
 *
 * The inverse of `nextPeriodStart`, and it exists here rather than in the one screen that steps
 * backwards through months for the reason that function does: month arithmetic written locally is
 * where December and January get mishandled, and a period is the unit this product locks, pays
 * and reports in.
 */
export function previousPeriodStart(start: PeriodStart): PeriodStart {
  const [yearText, monthText] = start.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  return month === 1
    ? `${String(year - 1).padStart(4, '0')}-12-01`
    : `${yearText}-${String(month - 1).padStart(2, '0')}-01`;
}

/**
 * How many months from one period to another. `2026-08-01` → `2026-11-01` is 3.
 *
 * SIGNED, and negative is a real answer rather than an error: the caller asking "how far into the
 * series is this month" needs to be told when the month is BEFORE the series started, and clamping
 * to zero here would turn that into an off-by-one at every call site instead.
 *
 * Arithmetic on the year and month, never on `Date`. Adding 90 days to the 31st of a month lands
 * wherever the calendar happens to put it, and a month count derived from a millisecond difference
 * is wrong across DST and across February — neither of which is a bug you find in a unit test
 * written in the same week as the code.
 */
export function monthsBetween(from: PeriodStart, to: PeriodStart): number {
  const start = monthIndex(from);
  const end = monthIndex(to);
  return end - start;
}

/** Months since year zero — the only form in which month arithmetic is just subtraction. */
function monthIndex(period: PeriodStart): number {
  const [yearText, monthText] = period.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    throw new RangeError(`Not a period start: ${JSON.stringify(period)}`);
  }
  return year * 12 + (month - 1);
}

/** The last calendar day of a period's month. `2026-08-01` → `2026-08-31`. */
export function periodEndsOn(start: PeriodStart): string {
  return previousDay(nextPeriodStart(start));
}

/** "August 2026" — for a heading, a lock confirmation, or an audit line. */
export function formatPeriod(start: PeriodStart): string {
  const [year, month] = start.split('-');
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${names[Number(month) - 1] ?? month} ${year}`;
}

/** May a journal be posted into a period in this state? */
export function acceptsPostings(status: PeriodStatus): boolean {
  return status !== 'locked';
}

// ── Routing ──────────────────────────────────────────────────────────────────

export interface PeriodSummary {
  startsOn: PeriodStart;
  status: PeriodStatus;
}

export interface PeriodRouting {
  /** The period this journal actually posts into. */
  startsOn: PeriodStart;
  /**
   * True when the journal was dated into a closed month and has been carried forward.
   *
   * It appears on the receiving period's report as a prior-period adjustment, which is how an
   * auditor sees that something arrived after the books were signed — rather than the figure
   * simply being different from the one they were given.
   */
  lateItem: boolean;
}

/**
 * The most recently signed-off month, or null if nothing has ever been locked.
 *
 * The pivot every "may this be changed" question turns on, which is why it is one function
 * rather than the same reduce written wherever it is needed. Months are signed off in order,
 * so ONE date divides the books: at or before it is reported and settled, after it is still
 * being worked.
 */
export function latestLockedPeriod(periods: readonly PeriodSummary[]): PeriodStart | null {
  const locked = periods.filter((p) => p.status === 'locked').map((p) => p.startsOn);
  return locked.length > 0 ? locked.reduce((a, b) => (a > b ? a : b)) : null;
}

/**
 * Where a journal dated `date` should post, given the periods that exist.
 *
 * The rule is deliberately about the LATEST locked month rather than about the journal's own
 * month. A journal dated into an unlocked May, when June has been locked, must not post to
 * May: June was signed off on an opening balance that May would change underneath it. Anything
 * at or before the last lock goes forward.
 *
 * Returns the period to use; the caller creates it if it does not exist yet. Periods are
 * created on demand rather than opened by an administrator — an accountant should not have to
 * declare that September exists before recording September's rent.
 */
export function routeToPeriod(date: string, periods: readonly PeriodSummary[]): PeriodRouting {
  const target = periodStartFor(date);
  const latestLocked = latestLockedPeriod(periods);

  if (latestLocked === null || target > latestLocked) {
    return { startsOn: target, lateItem: false };
  }

  // At or behind the last lock. Carry forward to the first month after it that is still open.
  const candidates = periods
    .filter((p) => p.startsOn > latestLocked && acceptsPostings(p.status))
    .map((p) => p.startsOn)
    .sort();

  return { startsOn: candidates[0] ?? nextPeriodStart(latestLocked), lateItem: true };
}

/**
 * The last day a journal entered today may be dated: the end of NEXT month.
 *
 * A journal records something that happened, and money does not arrive or leave in advance of
 * itself. Next month is allowed because rent is payable in advance and an agency entering the
 * 1st's payments on the 30th is ordinary. Beyond that, a date is almost always a typo — 2027 for
 * 2026 — and a typo is not harmless: it opens a month a year away, and until the invariant-3
 * check became dated it was money that did not exist funding payments made today.
 */
export function latestPostableDate(today: string): string {
  return periodEndsOn(nextPeriodStart(periodStartFor(today)));
}

/** Problems with the date a person typed on a journal. Empty means it may be used. */
export function journalDateIssues(date: string, today: string): string[] {
  const latest = latestPostableDate(today);
  return date > latest
    ? [
        `${formatNzDate(date)} is too far ahead — entries can be dated up to ${formatNzDate(latest)}. Enter it when it happens.`,
      ]
    : [];
}

// ── Locking ──────────────────────────────────────────────────────────────────

/**
 * One trust bank account's state at the month close.
 *
 * `reconciledTo` is the LAST day INSIDE this month that the account has a reconciliation for.
 * A later reconciliation does not count: signing August off against September's figures would
 * be signing a document that includes a month nobody has reviewed.
 */
export interface MonthCloseAccountState {
  name: string;
  reconciledTo: string | null;
}

/** A break inside the month that nobody has explained — §7 step 2. */
export interface UnexplainedBreak {
  ref: string;
  reconciledOn: string;
  accountName: string;
  code: BreakCode;
  differenceCents: Money;
}

/**
 * What the reconciliation says about the month being closed — §7 steps 1 and 2.
 *
 * The whole of "1. Month end: run the three-way reconciliation. 2. Any break must be resolved or
 * explicitly explained with a reason code", reduced to the two facts that decide it: is every
 * trust bank account reconciled through the last day of the month, and does any break inside it
 * still lack an explanation.
 *
 * `accounts` may be EMPTY, and that is not a failure. An organisation with no trust bank account
 * has nothing to reconcile — the first month of a new agency, before the account is set up — and
 * refusing to close it would leave a month that can never be signed and blocks every one after it.
 */
export interface MonthCloseState {
  accounts: readonly MonthCloseAccountState[];
  unexplainedBreaks: readonly UnexplainedBreak[];
}

export interface LockRequest {
  period: PeriodSummary;
  /** Every period in the organisation, so the order of closing can be checked. */
  all: readonly PeriodSummary[];
  actorRoles: readonly string[];
  /** The agency's grant on the actor's membership — see `canClosePeriods`. */
  actorMayClosePeriods: boolean;
  /**
   * The suspense balance as at the close, in cents.
   *
   * TRUST_ACCOUNTING.md §5: suspense is a holding pen, not a resting place, and a month may not
   * be signed off over unidentified money without somebody saying why in writing. Required
   * rather than optional — a caller that forgets it would silently close months over suspense,
   * and the whole discipline would lapse without a single test failing.
   */
  suspenseCents: bigint;
  /**
   * Why the month is being closed over a non-zero suspense balance, when it is.
   *
   * Null means no override offered, which is correct whenever suspense is zero.
   */
  suspenseOverrideReason?: string | null;
  /**
   * The reconciliation's account of the month — §7 steps 1 and 2.
   *
   * Required, for the same reason `suspenseCents` is: a caller that could omit it would close
   * months against nothing at all, and the sequence the whole section describes — reconcile,
   * explain what did not agree, then sign — would quietly become "sign".
   */
  close: MonthCloseState;
}

/**
 * The refusal for someone without the permission. One sentence for both directions, because the
 * permission is one thing — and it says where the permission is given, which is the only useful
 * thing to tell someone who has just been refused it.
 */
export const NOT_PERMITTED_TO_CLOSE =
  'You do not have permission to close or reopen months — an administrator can allow it in Settings › Users';

/** Long enough to be an explanation, like an unlock reason. It goes in the audit log. */
export const MIN_SUSPENSE_OVERRIDE_REASON_LENGTH = 15;

/**
 * Problems with locking a period. Empty means it may be locked.
 *
 * Same idiom as the rest of core: a list of sentences, not a thrown error, because each one is
 * something the person at the screen can act on.
 */
export function lockIssues(request: LockRequest): string[] {
  const issues: string[] = [];
  const {
    period,
    all,
    actorRoles,
    actorMayClosePeriods,
    suspenseCents,
    suspenseOverrideReason,
    close,
  } = request;

  if (!canClosePeriods(actorRoles, actorMayClosePeriods)) {
    issues.push(NOT_PERMITTED_TO_CLOSE);
  }

  if (period.status === 'locked') {
    issues.push(`${formatPeriod(period.startsOn)} is already closed`);
  }

  // Months close in order. A locked September above an open August would mean September was
  // signed off on an opening balance that August can still change.
  const earlierOpen = all
    .filter((p) => p.startsOn < period.startsOn && p.status !== 'locked')
    .map((p) => p.startsOn)
    .sort();

  if (earlierOpen.length > 0) {
    issues.push(
      `Close ${formatPeriod(earlierOpen[0] as PeriodStart)} first — months close in order`,
    );
  }

  /*
   * The reconciliation — §7 steps 1 and 2, in the order the section states them.
   *
   * Step 1 is that the month has been reconciled at all, and "reconciled" now means daily: the
   * figures a month closes on are the ones its last day closed on. So every trust bank account
   * has to have a reconciliation dated on the last day of the month, not merely somewhere in it —
   * an account reconciled to the 12th has three unreviewed weeks in the balance being signed.
   */
  const endsOn = periodEndsOn(period.startsOn);

  for (const account of close.accounts) {
    if (account.reconciledTo === endsOn) continue;

    issues.push(
      account.reconciledTo === null
        ? `${account.name} has not been reconciled in ${formatPeriod(period.startsOn)} — reconcile it through ${endsOn} before closing the month`
        : `${account.name} is reconciled to ${account.reconciledTo} — reconcile it through ${endsOn} before closing the month`,
    );
  }

  /*
   * Step 2: "any break must be resolved or explicitly explained with a reason code".
   *
   * A break that was resolved leaves no unexplained entry here — resolving it is what the next
   * day's clean reconciliation records. What remains is a day that closed while the books did not
   * agree, and signing the month without a word about it is the thing being prevented. The note
   * does not make the figures right; it makes the sign-off honest about them.
   */
  for (const item of close.unexplainedBreaks) {
    issues.push(`${describeBreak(item)} — resolve it or record a reason code against it`);
  }

  /*
   * Suspense — §5.
   *
   * Not a refusal, an override. Money that cannot be identified by month end is a real thing
   * that happens, and a rule that simply forbade closing would be worked around by posting the
   * balance somewhere it does not belong — which is worse, because then it is invisible. So the
   * month may close, and the reason it closed that way is written down.
   *
   * The reason is REQUIRED at length for the same purpose an unlock reason is: it is what an
   * auditor reads when they ask why a signed-off month was holding money belonging to nobody.
   */
  if (suspenseCents !== 0n) {
    const reason = suspenseOverrideReason?.trim() ?? '';

    if (reason === '') {
      issues.push(
        `Suspense holds ${formatAmount(abs(suspenseCents))} — identify it, or give a reason for closing the month over it`,
      );
    } else if (reason.length < MIN_SUSPENSE_OVERRIDE_REASON_LENGTH) {
      issues.push(
        `Give a reason of at least ${MIN_SUSPENSE_OVERRIDE_REASON_LENGTH} characters — it goes in the audit log`,
      );
    }
  }

  return issues;
}

export interface UnlockRequest {
  period: PeriodSummary;
  /** Every period in the organisation. */
  all: readonly PeriodSummary[];
  actorRoles: readonly string[];
  actorMayClosePeriods: boolean;
  /** Who locked it. Reopening needs a SECOND person with the permission — see below. */
  lockedByUserId: string | null;
  actorUserId: string;
  reason: string;
}

export const MIN_UNLOCK_REASON_LENGTH = 15;

/**
 * Problems with unlocking a period.
 *
 * Four eyes: the person who signed a month off cannot be the person who reopens it. That is
 * the whole control — a single compromised or careless closer should not be able to
 * close a month, reopen it, change it, and close it again with nobody else involved.
 *
 * TRUST_ACCOUNTING.md §7 calls unlocking "loudly audited". The reason has a length floor for
 * the same purpose the support-access reason does: it is what an auditor reads when they ask
 * why the signed-off figures moved.
 */
export function unlockIssues(request: UnlockRequest): string[] {
  const issues: string[] = [];
  const { period, all, actorRoles, actorMayClosePeriods, lockedByUserId, actorUserId, reason } =
    request;

  if (!canClosePeriods(actorRoles, actorMayClosePeriods)) {
    issues.push(NOT_PERMITTED_TO_CLOSE);
  }

  if (period.status !== 'locked') {
    issues.push(`${formatPeriod(period.startsOn)} is not closed`);
  }

  if (lockedByUserId !== null && lockedByUserId === actorUserId) {
    issues.push(
      'You closed this month, so someone else must reopen it — a second person with permission to close months is required',
    );
  }

  if (reason.trim().length < MIN_UNLOCK_REASON_LENGTH) {
    issues.push(
      `Give a reason of at least ${MIN_UNLOCK_REASON_LENGTH} characters — it goes in the audit log`,
    );
  }

  // Reopening a month below a locked one would change what that locked month opened with.
  const laterLocked = all
    .filter((p) => p.startsOn > period.startsOn && p.status === 'locked')
    .map((p) => p.startsOn)
    .sort();

  if (laterLocked.length > 0) {
    issues.push(
      `Reopen ${formatPeriod(laterLocked[laterLocked.length - 1] as PeriodStart)} first — it was closed on this month's closing balance`,
    );
  }

  return issues;
}
